import type { DesignBoardViewModel } from '@rocco/ui/design-board/view-models/design-board';
import { BOARD_TERMS } from '~/config/mood-board-terminology';
import type {
    FollowSelectorSection,
    FollowSelectorItem,
    FollowSelectorThumbnail,
} from '../types/follow-selector';

/**
 * Build the thumbnail for a board, preferring the dedicated board-level thumbnail
 * and falling back to the first pin's image.
 */
export const buildThumbnail = (
    board: DesignBoardViewModel,
): FollowSelectorThumbnail | undefined => {
    if (board.thumbnail?.url) {
        return {
            src: board.thumbnail.url,
            type: board.thumbnail.type,
            backgroundColor: board.thumbnail.backgroundColor,
            ...(board.thumbnail.multiplied && { multiplied: true }),
            ...(board.thumbnail.isClearCut && { isClearCut: true }),
        };
    }

    const firstPin = board.pins.find((pin) => pin.image);
    if (!firstPin) {
        return undefined;
    }

    return {
        src: firstPin.image,
        type: firstPin.type,
        backgroundColor: firstPin.backgroundColor,
        ...(firstPin.multiplied && { multiplied: true }),
        ...(firstPin.isClearCut && { isClearCut: true }),
    };
};

/**
 * Maps a DesignBoardViewModel to a FollowSelectorItem.
 */
const mapToSelectorItem = (
    board: DesignBoardViewModel,
    isSavedToBoard: (id: number) => boolean,
): FollowSelectorItem => {
    const isSaved = isSavedToBoard(board.id);

    const thumbnailUrls = board.pins.map((pin) => pin.image).filter(Boolean);
    const thumbnail = buildThumbnail(board);

    return {
        id: board.id,
        title: board.title,
        projectName: board.project?.title,
        thumbnailUrls,
        thumbnail,
        entityType: 'moodboard',
        isSaved,
        urlSegment: board.urlSegment,
        projectId: board.project?.id,
        projectUrlSegment: board.project?.urlSegment,
        isPersonal: board.isPersonalMoodBoard ?? false,
    };
};

/**
 * Builds sections for the FollowSelector from boards and search results.
 * Returns a single "My Mood Boards" section sorted by LastActivity DESC
 * (the server handles the sort order).
 *
 * @param boards - All paginated boards (sorted by LastActivity DESC from server)
 * @param searchResults - Search results (if searching)
 * @param searchQuery - Current search query
 * @param isSavedToBoard - Function to check if a pin is saved to a board
 * @returns Array of sections to display
 */
export const buildSections = (
    boards: DesignBoardViewModel[],
    searchResults: DesignBoardViewModel[] | null,
    searchQuery: string,
    isSavedToBoard: (id: number) => boolean,
): FollowSelectorSection[] => {
    // If searching, show search results only
    if (searchQuery.trim() && searchResults) {
        return [
            {
                id: 'search-results',
                title: 'Search Results',
                items: searchResults.map((board) =>
                    mapToSelectorItem(board, isSavedToBoard),
                ),
                entityType: 'moodboard' as const,
            },
        ].filter((s) => s.items.length > 0);
    }

    if (boards.length === 0) {
        return [];
    }

    return [
        {
            id: 'personal-moodboards',
            title: BOARD_TERMS.myBoards,
            items: boards.map((board) =>
                mapToSelectorItem(board, isSavedToBoard),
            ),
            entityType: 'moodboard' as const,
        },
    ];
};
